import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard,
  KeyRound,
  Sparkles,
  BarChart3,
  Menu,
  Zap,
  ExternalLink,
  Copy,
  Check,
  Users,
  Key,
  LogOut,
  ShieldCheck,
  Activity,
  Plug,
  Rocket,
  Bot,
  Server,
} from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AuthProvider, useAuth } from "@/components/AuthContext";
import LoginPage from "@/components/LoginPage";
import Dashboard from "@/components/Dashboard";
import ApiKeysPage from "@/components/ApiKeysPage";
import PlaygroundPage from "@/components/PlaygroundPage";
import UsageHistoryPage from "@/components/UsageHistoryPage";
import UserManagementPage from "@/components/UserManagementPage";
import ApiManagementPage from "@/components/ApiManagementPage";
import LiveMonitorPage from "@/components/LiveMonitorPage";
import PluginsPage from "@/components/PluginsPage";
import GettingStartedPage from "@/components/GettingStartedPage";
import AgentsPage from "@/components/AgentsPage";
import McpServersPage from "@/components/McpServersPage";
import AgentBuildPublicPage from "@/components/AgentBuildPublicPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ALL_NAV_ITEMS = [
  { label: "Get Started", icon: Rocket, route: "/get-started", permission: "dashboard", testId: "sidebar-nav-get-started" },
  { label: "Dashboard", icon: LayoutDashboard, route: "/", permission: "dashboard", testId: "sidebar-nav-dashboard" },
  { label: "Live Monitor", icon: Activity, route: "/monitor", permission: "dashboard", testId: "sidebar-nav-monitor" },
  { label: "Plugins", icon: Plug, route: "/plugins", permission: "api_keys", testId: "sidebar-nav-plugins" },
  { label: "Agents", icon: Bot, route: "/agents", permission: "agents", testId: "sidebar-nav-agents" },
  { label: "MCP Servers", icon: Server, route: "/mcp-servers", permission: "agents", testId: "sidebar-nav-mcp" },
  { label: "API Keys", icon: KeyRound, route: "/keys", permission: "api_keys", testId: "sidebar-nav-api-keys" },
  { label: "Playground", icon: Sparkles, route: "/playground", permission: "playground", testId: "sidebar-nav-playground" },
  { label: "Usage History", icon: BarChart3, route: "/usage", permission: "usage_history", testId: "sidebar-nav-usage-history" },
  { label: "API Management", icon: Key, route: "/api-management", permission: "api_management", testId: "sidebar-nav-api-mgmt" },
  { label: "User Management", icon: Users, route: "/users", permission: "user_management", testId: "sidebar-nav-users" },
];

function Sidebar({ currentPath, onNavigate }) {
  const { user, logout, hasPermission } = useAuth();
  const [copied, setCopied] = useState(false);
  const endpointUrl = `${BACKEND_URL}/api/v1/chat/completions`;

  const navItems = ALL_NAV_ITEMS.filter(item => hasPermission(item.permission));

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(endpointUrl);
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = endpointUrl;
      textarea.style.position = "fixed";
      textarea.style.left = "-9999px";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    }
    setCopied(true);
    toast.success("Endpoint URL copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="sidebar">
      <div className="sidebar-logo">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
            <Zap className="w-4 h-4 text-white" />
          </div>
          <div>
            <h1 className="text-sm font-semibold text-foreground">LLM Hub</h1>
            <p className="text-[11px] text-muted-foreground">Intelligent API Router</p>
          </div>
        </div>
      </div>

      <nav className="sidebar-nav">
        {navItems.map((item) => (
          <button
            key={item.route}
            data-testid={item.testId}
            className={`sidebar-nav-item ${currentPath === item.route ? "active" : ""}`}
            onClick={() => onNavigate(item.route)}
          >
            <item.icon className="w-[18px] h-[18px]" />
            {item.label}
          </button>
        ))}
      </nav>

      <div className="sidebar-footer" data-testid="sidebar-unified-endpoint">
        <p className="text-[11px] font-medium text-muted-foreground mb-2 uppercase tracking-wider">Unified API Endpoint</p>
        <div className="flex items-center gap-1">
          <code className="text-[10px] font-mono bg-muted px-2 py-1 rounded flex-1 truncate">
            {endpointUrl}
          </code>
          <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={handleCopy}>
            {copied ? <Check className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3" />}
          </Button>
        </div>

        {user && (
          <div className="mt-3 pt-3 border-t border-border/50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5 min-w-0">
                <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-[10px] font-bold text-primary shrink-0">
                  {(user.name || user.email)[0].toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] font-medium truncate">{user.name || user.email}</p>
                  <div className="flex items-center gap-1">
                    {user.role === "superadmin" && <ShieldCheck className="w-2.5 h-2.5 text-primary" />}
                    <p className="text-[9px] text-muted-foreground capitalize">{user.role}</p>
                  </div>
                </div>
              </div>
              <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={logout} title="Sign out" data-testid="logout-btn">
                <LogOut className="w-3 h-3" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function MobileHeader({ currentPath, onNavigate }) {
  const { user, logout, hasPermission } = useAuth();
  const [open, setOpen] = useState(false);
  const navItems = ALL_NAV_ITEMS.filter(item => hasPermission(item.permission));
  const currentItem = navItems.find(i => i.route === currentPath);

  return (
    <div className="lg:hidden flex items-center justify-between px-4 py-3 border-b bg-white/80 backdrop-blur sticky top-0 z-50">
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 rounded-lg bg-primary flex items-center justify-center">
          <Zap className="w-3.5 h-3.5 text-white" />
        </div>
        <span className="text-sm font-semibold">{currentItem?.label || "LLM Hub"}</span>
      </div>
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={logout} title="Sign out">
          <LogOut className="w-4 h-4" />
        </Button>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon">
              <Menu className="w-5 h-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-[260px] p-0">
            <div className="p-4 border-b">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                  <Zap className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h1 className="text-sm font-semibold">LLM Hub</h1>
                  <p className="text-[11px] text-muted-foreground">Intelligent API Router</p>
                </div>
              </div>
            </div>
            <nav className="p-3 flex flex-col gap-1">
              {navItems.map((item) => (
                <button
                  key={item.route}
                  className={`sidebar-nav-item ${currentPath === item.route ? "active" : ""}`}
                  onClick={() => { onNavigate(item.route); setOpen(false); }}
                >
                  <item.icon className="w-[18px] h-[18px]" />
                  {item.label}
                </button>
              ))}
            </nav>
          </SheetContent>
        </Sheet>
      </div>
    </div>
  );
}

function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = useAuth();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchProviders = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/providers`);
      setProviders(res.data || []);
    } catch (err) {
      console.error("Failed to fetch providers:", err);
      setProviders([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);

  const handleNavigate = (route) => {
    navigate(route);
  };

  return (
    <div className="app-shell app-noise">
      <Sidebar currentPath={location.pathname} onNavigate={handleNavigate} />

      <main className="main-content">
        <MobileHeader currentPath={location.pathname} onNavigate={handleNavigate} />
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
          >
            <Routes location={location}>
              <Route path="/get-started" element={hasPermission("dashboard") ? <GettingStartedPage api={API} onNavigate={handleNavigate} /> : <NoAccess />} />
              <Route path="/" element={hasPermission("dashboard") ? <Dashboard providers={providers} loading={loading} onRefresh={fetchProviders} api={API} /> : <NoAccess />} />
              <Route path="/monitor" element={hasPermission("dashboard") ? <LiveMonitorPage api={API} /> : <NoAccess />} />
              <Route path="/plugins" element={hasPermission("api_keys") ? <PluginsPage api={API} /> : <NoAccess />} />
              <Route path="/agents" element={hasPermission("agents") ? <AgentsPage api={API} /> : <NoAccess />} />
              <Route path="/mcp-servers" element={hasPermission("agents") ? <McpServersPage api={API} /> : <NoAccess />} />
              <Route path="/keys" element={hasPermission("api_keys") ? <ApiKeysPage providers={providers} onRefresh={fetchProviders} api={API} /> : <NoAccess />} />
              <Route path="/playground" element={hasPermission("playground") ? <PlaygroundPage providers={providers} api={API} /> : <NoAccess />} />
              <Route path="/usage" element={hasPermission("usage_history") ? <UsageHistoryPage api={API} /> : <NoAccess />} />
              <Route path="/api-management" element={hasPermission("api_management") ? <ApiManagementPage api={API} /> : <NoAccess />} />
              <Route path="/users" element={hasPermission("user_management") ? <UserManagementPage api={API} /> : <NoAccess />} />
            </Routes>
          </motion.div>
        </AnimatePresence>
      </main>
    </div>
  );
}

function NoAccess() {
  return (
    <div className="flex items-center justify-center h-[60vh]">
      <div className="text-center">
        <ShieldCheck className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
        <h2 className="text-lg font-semibold">Access Denied</h2>
        <p className="text-sm text-muted-foreground mt-1">You don't have permission to view this page.</p>
      </div>
    </div>
  );
}

function AuthGate() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center mx-auto mb-3">
            <Zap className="w-5 h-5 text-white" />
          </div>
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <LoginPage />;
  }

  return <AppShell />;
}

function App() {
  return (
    <BrowserRouter>
      {/* Toaster mounted at root so it works on the login page (pre-auth) too */}
      <Toaster position="bottom-right" richColors />
      <Routes>
        {/* Public route — no auth required */}
        <Route path="/agent-build/:token" element={<AgentBuildPublicPage />} />
        {/* Everything else is auth-gated */}
        <Route
          path="*"
          element={
            <AuthProvider>
              <AuthGate />
            </AuthProvider>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
